import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTicketNotifications } from "../hooks/useApi";

interface NotificationItem {
  ticket_id: number;
  subject: string;
  unread_count: number;
  last_message: string | null;
  last_message_at: string | null;
  updated_at: string;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "هم‌اکنون";
  if (minutes < 60) return `${minutes} دقیقه پیش`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ساعت پیش`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} روز پیش`;
  return new Date(iso).toLocaleDateString("fa-IR");
}

const DROPDOWN_WIDTH = 320;
const MARGIN = 8;

export default function NotificationBell() {
  const { data: notifications = [], isLoading } = useTicketNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  const unreadTotal = notifications.reduce((sum: number, n: NotificationItem) => sum + n.unread_count, 0);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    const reposition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const width = Math.min(DROPDOWN_WIDTH, window.innerWidth - MARGIN * 2);
      const left = Math.min(Math.max(rect.left, MARGIN), window.innerWidth - width - MARGIN);
      setDropdownPos({ top: rect.bottom + MARGIN, left });
    };
    if (open) {
      reposition();
      window.addEventListener("resize", reposition);
      window.addEventListener("scroll", reposition, true);
    }
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open]);

  const goToTicket = (ticketId: number) => {
    setOpen(false);
    navigate(`/support?ticket=${ticketId}`);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="اعلان‌ها"
        title="اعلان‌ها"
        className="relative flex items-center justify-center w-9 h-9 rounded-full bg-surface-muted border border-line text-text-secondary hover:text-text-main hover:border-primary/40 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadTotal > 0 && (
          <span className="absolute -top-1 -left-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadTotal > 99 ? "99+" : unreadTotal}
          </span>
        )}
      </button>

      {open && dropdownPos && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="fixed z-40 rounded-[12px] overflow-hidden bg-surface border border-line shadow-modal"
            style={{ top: dropdownPos.top, left: dropdownPos.left, width: Math.min(DROPDOWN_WIDTH, window.innerWidth - MARGIN * 2) }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <span className="text-small font-semibold text-text-main">اعلان‌های پشتیبانی</span>
              {unreadTotal > 0 && (
                <button
                  type="button"
                  onClick={() => navigate("/support")}
                  className="text-tiny text-primary hover:text-primary/80 transition-colors"
                >
                  مشاهده همه تیکت‌ها
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="p-4 space-y-2">
                <div className="h-12 bg-surface-hover rounded animate-pulse" />
                <div className="h-12 bg-surface-hover rounded animate-pulse" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <p className="text-small text-text-tertiary">اعلان جدیدی ندارید</p>
              </div>
            ) : (
              <div className="max-h-[320px] overflow-y-auto">
                {(notifications as NotificationItem[]).map((n) => (
                  <button
                    key={n.ticket_id}
                    type="button"
                    onClick={() => goToTicket(n.ticket_id)}
                    className="w-full text-right px-4 py-3 hover:bg-surface-hover transition-colors border-b border-line last:border-b-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-small font-semibold text-text-main truncate">{n.subject}</p>
                      <span className="shrink-0 rounded-full bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5">
                        {n.unread_count} پاسخ جدید
                      </span>
                    </div>
                    <p className="text-tiny text-text-secondary mt-1 truncate">
                      {n.last_message || "پاسخ جدید از پشتیبانی"}
                    </p>
                    <p className="text-tiny text-text-tertiary mt-1">{timeAgo(n.updated_at)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
